import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { VALIDATION_RULES } from '@onboarding-dashboard/shared';
import { useAuth } from '../../contexts/AuthContext.js';

import { VersionBadge } from '../../components/common/VersionBadge.js';
import loginTemplate from './login.html?raw';
import loginLogoUrl from '../../assets/eXeX_Title_White_with_name.png';
import './login.css';

export function LoginPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const authInitDone = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle, isAuthenticated, isLoading, error, clearError } = useAuth();

  // Track when auth initialization completes (isLoading starts true, becomes false once)
  if (!isLoading) authInitDone.current = true;

  const token = searchParams.get('token');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Display API error
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const errorDiv = container.querySelector<HTMLDivElement>('#login-error');
    const errorMsg = container.querySelector<HTMLSpanElement>('#login-error-message');
    if (errorDiv && errorMsg) {
      if (error) {
        errorMsg.textContent = error;
        errorDiv.style.display = 'block';
      } else {
        errorDiv.style.display = 'none';
        errorMsg.textContent = '';
      }
    }
  }, [error]);

  // Toggle loading state on submit button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const loginBtn = container.querySelector<HTMLButtonElement>('#login-submit');
    if (loginBtn) {
      loginBtn.disabled = isLoading;
      loginBtn.textContent = isLoading ? 'Signing in...' : 'Sign In';
    }
  }, [isLoading]);

  // Login form validation
  const validateLoginForm = useCallback((): boolean => {
    const container = containerRef.current;
    if (!container) return false;
    let isValid = true;

    for (const field of ['email', 'password']) {
      const errorEl = container.querySelector<HTMLSpanElement>(`#login-${field}-error`);
      const inputEl = container.querySelector<HTMLInputElement>(`#login-${field}`);
      if (errorEl) errorEl.textContent = '';
      inputEl?.classList.remove('input-error');
    }

    const email = (container.querySelector<HTMLInputElement>('#login-email')?.value ?? '').trim();
    const password = container.querySelector<HTMLInputElement>('#login-password')?.value ?? '';

    if (!email || !VALIDATION_RULES.EMAIL_REGEX.test(email)) {
      setFieldError(container, 'login-email', 'A valid email is required');
      isValid = false;
    }
    if (!password) {
      setFieldError(container, 'login-password', 'Password is required');
      isValid = false;
    }

    return isValid;
  }, []);

  // Set logo, attach form handler, render Google button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set logo src
    const logoEl = container.querySelector<HTMLImageElement>('#login-logo');
    if (logoEl) logoEl.src = loginLogoUrl;

    // Login form handler
    const loginFormEl = container.querySelector<HTMLFormElement>('#login-form');
    const handleLoginSubmit = (e: Event): void => {
      e.preventDefault();
      clearError();
      if (!validateLoginForm()) return;

      const email = (container.querySelector<HTMLInputElement>('#login-email')?.value ?? '').trim();
      const password = container.querySelector<HTMLInputElement>('#login-password')?.value ?? '';
      void login(email, password);
    };
    loginFormEl?.addEventListener('submit', handleLoginSubmit);

    return () => {
      loginFormEl?.removeEventListener('submit', handleLoginSubmit);
    };
  }, [login, clearError, validateLoginForm, isLoading]);

  // Render Google sign-in button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // Don't attempt rendering until the login HTML is in the DOM
    const googleContainer = container.querySelector<HTMLDivElement>(
      '#google-signin-button-container'
    );
    if (!googleContainer) return;

    let cancelled = false;
    const renderGoogleSignin = (): void => {
      if (cancelled) return;
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential: string }) => {
            void loginWithGoogle(response.credential);
          },
        });
        window.google.accounts.id.renderButton(googleContainer, {
          theme: 'outline',
          size: 'large',
          width: googleContainer.offsetWidth || undefined,
          text: 'signin_with',
        });
      }
    };

    if (window.google?.accounts?.id) {
      renderGoogleSignin();
    } else if (window.__gsiReady) {
      void window.__gsiReady.then(renderGoogleSignin);
    }

    return () => {
      cancelled = true;
    };
  }, [loginWithGoogle, isLoading]);

  // If there's an invite token, redirect to signup page
  if (token) {
    return <Navigate to={`/signup?token=${encodeURIComponent(token)}`} replace />;
  }

  // Don't render page until auth init completes (prevents flash before redirect)
  if (!authInitDone.current) return <div />;

  return (
    <>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: loginTemplate }} />
      <VersionBadge />
    </>
  );
}

function setFieldError(container: HTMLDivElement, field: string, message: string): void {
  const errorEl = container.querySelector<HTMLSpanElement>(`#${field}-error`);
  const inputEl = container.querySelector<HTMLInputElement>(`#${field}`);
  if (errorEl) errorEl.textContent = message;
  inputEl?.classList.add('input-error');
}
